import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import InstagramIcon from '@mui/icons-material/Instagram';

interface VenueInstagramSectionProps {
  instagramHandle: string | null | undefined;
  venueName: string;
}

export function VenueInstagramSection({
  instagramHandle,
  venueName: _venueName,
}: VenueInstagramSectionProps) {
  if (!instagramHandle) return null;

  const handle = instagramHandle.replace(/^@/, '');
  const profileUrl = `https://www.instagram.com/${handle}/`;

  const tiles = [
    { gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
    { gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
    { gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
  ];

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(45deg, #FCAF45, #F56040, #C13584, #833AB4)',
          borderRadius: 2,
          p: 2,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <InstagramIcon sx={{ color: '#fff', fontSize: 28 }} />
        <Box>
          <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
            Latest on Instagram
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.85)',
              fontFamily: '"IBM Plex Mono", monospace',
              fontWeight: 700,
            }}
          >
            @{handle}
          </Typography>
        </Box>
      </Box>

      {/* Tile grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2 }}>
        {tiles.map((tile, i) => (
          <Box
            key={i}
            component="a"
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`View @${handle} on Instagram`}
            sx={{
              position: 'relative',
              display: 'block',
              paddingBottom: '100%',
              borderRadius: 1.5,
              overflow: 'hidden',
              background: tile.gradient,
              textDecoration: 'none',
              '&:hover .tile-overlay': { opacity: 1 },
            }}
          >
            {/* Overlay — always visible at 0.7, full on hover (touch-safe) */}
            <Box
              className="tile-overlay"
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                opacity: 0.7,
                transition: 'opacity 0.2s',
              }}
            >
              <CameraAltIcon sx={{ color: '#fff', fontSize: 28 }} />
              <Typography
                variant="caption"
                sx={{ color: '#fff', textAlign: 'center', px: 1, fontWeight: 600 }}
              >
                See our latest posts
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* CTA row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontWeight: 700,
            color: 'text.secondary',
          }}
        >
          @{handle}
        </Typography>
        <MuiButton
          component="a"
          href={profileUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          size="small"
          startIcon={<InstagramIcon />}
          sx={{
            borderColor: '#C13584',
            color: '#C13584',
            '&:hover': {
              borderColor: '#833AB4',
              color: '#833AB4',
              background: 'rgba(193,53,132,0.04)',
            },
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          Follow on Instagram
        </MuiButton>
      </Box>
    </Box>
  );
}
